import AttorneyHubAPIService from "_shared/_api/attorney-hub-api-service";
import { EntityMemberJSON } from "_shared/entity-members/entity-member-schema";
import Guid from "_shared/guids/guid";
import GuidSchema from "_shared/guids/guid-schema";
import axios, { AxiosHeaders, CanceledError, HttpStatusCode } from "axios";
import CreateUserEntityMemberInvitationAPIRequest from "legal-entities/invitations/member-invitations/email-based/_api/commands/create-user-entity-member-invitation-api-command";
import UserEntityMemberInvitation from "legal-entities/invitations/member-invitations/email-based/user-entity-member-invitation";
import { LegalEntityNotFoundError } from "legal-entities/legal-entity";
import Session from "users/__session/session";

export default class EntityMemberAPIService {
  private readonly headers: AxiosHeaders = new AxiosHeaders();
  private readonly session: Readonly<Session>;
  private readonly abortController?: AbortController;

  constructor(params: { session: Readonly<Session>; abortController?: AbortController }) {
    const { session, abortController } = params;
    if (!session.authToken) {
      throw new Error("Cannot create EntityMemberAPIService without session.");
    }

    this.session = session;
    this.abortController = abortController;

    this.headers.set("Authorization", `Bearer ${session.authToken.value}`);
  }

  async createUserEntityMemberInvitation(entityId: Guid, invitation: UserEntityMemberInvitation): Promise<Guid> {
    const url = new URL(
      `/legal-entities/${entityId.value}/user-entity-member-invitations`,
      AttorneyHubAPIService.apiBaseUrl,
    );
    try {
      const response = await axios.post(url.toString(), new CreateUserEntityMemberInvitationAPIRequest(invitation), {
        headers: this.headers.concat({ "Content-Type": "application/json-patch+json" }),
      });
      return new Guid(GuidSchema.deserialize(response.data));
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.BadRequest)
        throw new InvalidInvitationError(error.response.data);
      throw new EntityMemberAPIServiceError("createUserEntityMemberInvitation", error);
    }
  }

  async getMembersForLegalEntity(legalEntityId: Guid): Promise<EntityMemberJSON[]> {
    const url = new URL(`/legal-entities/${legalEntityId.value}/members/`, AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.get(url.toString(), {
        headers: this.headers,
        signal: this.abortController?.signal,
      });
      return response.data;
    } catch (error) {
      if (error instanceof CanceledError) throw error;
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.Forbidden)
        throw new UnauthorizedEntityMemberAccessError(legalEntityId);
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.NotFound)
        throw new LegalEntityNotFoundError(legalEntityId);
      throw new EntityMemberAPIServiceError("getLegalEntityMembersUserInfo", error);
    }
  }
}

export class InvalidInvitationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "InvalidInvitationError";
  }
}

export class InvalidMemberUpdateException extends Error {
  constructor(message: string) {
    super(message);
    this.name = "InvalidMemberUpdateException";
  }
}

export class EntityMemberAPIServiceError extends Error {
  methodName: string;
  error: unknown;
  constructor(methodName: string, error: unknown) {
    super(`Error in EntityMemberAPIService.${methodName}: ${error instanceof Error ? error.message : String(error)}`);
    this.methodName = methodName;
    this.error = error;
  }
}

export class UnauthorizedEntityMemberAccessError extends Error {
  constructor(entityId: Guid) {
    super(`Unauthorized access to legal entity members for entity ${entityId}`);
  }
}
